import type { Recipe } from '@/types'

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Search } from 'lucide-react'

import {
  Input,
  RecipeSearchCard,
  Spinner,
} from '@/components/ui'
import { useKeydownShortcut } from '@/hooks'
import { MixLayout } from '@/components/layout'
import { useWebSocketContext } from '@/contexts'
import { recipeService, mixService } from '@/services'
import { toast } from 'sonner'

export default function ScheduleAddRecipePage() {
  const { scheduleId = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mixes, isMix } = useWebSocketContext()

  const date = searchParams.get('date') || ''
  const slotId = searchParams.get('slot') || ''

  const mix = useMemo(() => {
    return isMix(scheduleId) ? mixes.find((m) => m.id === scheduleId) ?? null : null
  }, [scheduleId, isMix, mixes])

  const slot = useMemo(() => {
    return mix?.mealSlotOrder?.find((s) => s.id === slotId) ?? null
  }, [mix, slotId])

  const mixRecipeIds = useMemo(() => {
    return new Set((mix?.recipes || []).map((r) => r.id))
  }, [mix])

  // ── Search state ──
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Recipe[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [assigningId, setAssigningId] = useState<string | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const performSearch = useCallback(async (searchText: string) => {
    setIsSearching(true)
    setSearchError(null)
    try {
      const results = await recipeService.search(searchText)
      const sorted = results.sort((a, b) => {
        const countA = a.favoriteCount ?? 0
        const countB = b.favoriteCount ?? 0
        if (countB !== countA) return countB - countA
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      setSearchResults(sorted)
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  // Load recent recipes on mount
  useEffect(() => {
    performSearch('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced keyword search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [query, performSearch])

  const handleAssign = async (recipe: Recipe) => {
    if (!mix || !slot || !date || assigningId) return

    setAssigningId(recipe.id)

    try {
      // Always use appendRecipeToMix with slot context.
      // The Postgres RPC simply appends to the recipes/ingredients arrays,
      // so calling it again for the same recipe creates a distinct copy with
      // different slot tags — exactly what we want.
      await mixService.appendRecipeToMix(scheduleId, recipe.id, {
        scheduledAt: date,
        mealSlotId: slotId,
      })

      toast.success(`${recipe.name} assigned to ${slot.name}`)
      navigate(`/mixes/${scheduleId}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to assign recipe'
      toast.error(message)
    } finally {
      setAssigningId(null)
    }
  }

  const handleCancel = () => {
    navigate(`/mixes/${scheduleId}`)
  }

  useKeydownShortcut(
    { key: 'Escape' },
    handleCancel,
    'Cancel / Go back',
    'Return to the schedule view'
  )

  if (!mix || !slot || !date) {
    return (
      <MixLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Spinner className="size-12" />
        </div>
      </MixLayout>
    )
  }

  return (
    <MixLayout>
      <div className="flex flex-col min-h-0">
        {/* Sticky header */}
        <div className="sticky top-0 bg-background z-30 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">{mix.name}</h1>
              <p className="text-xs text-muted-foreground">
                Assign to {slot.name} · {date}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable search content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-24">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search by recipe name or ingredient..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setSearchError(null)
                }}
                className="pl-10"
              />
            </div>

            {isSearching && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Spinner className="size-4" />
                Searching...
              </div>
            )}

            {searchError && !isSearching && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 mb-3">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <h3 className="text-base font-medium text-foreground">Search failed</h3>
              </div>
            )}

            {!isSearching && !searchError && searchResults.length === 0 && query && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No recipes found for &quot;{query}&quot;.</p>
              </div>
            )}

            {!isSearching && !searchError && searchResults.length > 0 && (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {searchResults.map((recipe) => (
                  <div key={recipe.id} className="relative">
                    <RecipeSearchCard
                      recipe={recipe}
                      selected={false}
                      alreadyInMix={mixRecipeIds.has(recipe.id)}
                      assignable={true}
                      onToggle={() => handleAssign(recipe)}
                    />
                    {assigningId === recipe.id && (
                      <div className="absolute inset-0 bg-background/70 rounded-lg flex items-center justify-center z-20">
                        <Spinner className="size-6" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MixLayout>
  )
}
